import { twMerge } from "tailwind-merge";

export type NameAvatarProps = {
    /** The person's display name. Initials are taken from its first two words. */
    name?: string | null;
    className?: string;
}

/**
 * A person's initials, drawn as an avatar.
 *
 * Used wherever the API gives us a name but no photograph — which is everywhere
 * except the signed-in user's own `profileImage`. It exists so those places stop
 * reaching for a random stock face: an inspector's row used to render
 * `i.pravatar.cc`, putting a photo of a stranger next to a real ZCT officer's
 * name and leaking that name to a third-party service on every page view.
 */
export function initialsOfName(name?: string | null): string {
    const letters = (name ?? "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join("");

    return letters.toUpperCase() || "?";
}

const NameAvatar = ({ name, className }: NameAvatarProps) => (
    <span
        aria-hidden="true"
        className={twMerge(
            "w-8 h-8 shrink-0 rounded-full inline-flex justify-center items-center",
            "bg-primary-50 text-primary text-xs font-semibold",
            className
        )}
    >
        {initialsOfName(name)}
    </span>
);

export default NameAvatar;
